import { ReplicantMigrator } from '@play-co/replicant';

// IMPORTANT: Do *NOT* add any other imports. Migrations must stay
// self-contained forever — inline any helper you need.
//
// MIGRATION SAFETY — details: .claude/rules/replicant.md ("State Migrations")
// and https://docs.dev.gc-internal.net/replicant/basic-features/Migrations
//
// - Append-only: never edit or delete a released migration; fix bugs with a new
//   one. Sole exception: a critically broken migration may be stopped by
//   replacing its body with a no-op in a new release — that only prevents
//   FUTURE executions; states already migrated by the broken version keep its
//   shape and version, so plan a follow-up that tolerates or repairs them.
// - No rollback: reverting a release does not un-migrate anyone. Old clients
//   cannot read migrated player state, so a rollback locks migrated players out.
// - Mutate in place: applyMigration ignores the callback's return value —
//   `return { ...state, x }` silently does nothing.
// - Migrations run lazily, when a state is next fetched — possibly years from
//   now against very old states. Guard every property access.
// - Rollout: this is the PLAYER-state migrator. Adding a migration bumps the
//   player state version; old clients fail login/replicate on migrated state
//   with version_error (surfacing as AppInitFailed at next launch) until
//   rollout completes and old sessions cycle out. Ship migrations standalone,
//   roll out as fast as is safe; a two-step release does not prevent this.
// - Removing/renaming/retyping a schema field requires a two-step release:
//   (1) drop all code references, keep the schema field (@deprecated);
//   (2) next release drops the field and adds the cleanup migration.
// - Every new migration needs a numbered test file (N.description.test.ts)
//   including the empty-state {} case.

const migrator = new ReplicantMigrator();

/**
 * Remove gems it should be replaced by `premium` currency
 * Added default value for points
 */
migrator.addMigration(1, (state) => {
  // Remove gems it should be replaced by `premium` currency
  // Premium currency will be represented by: { free: number, paid: number }
  if (state?.inventory?.gems) {
    // If no state for premium currency let's add it
    if (state && state.premium === undefined) {
      state.inventory.premium = { free: 0, paid: 0 };
    }

    // Move gems to premium.free
    state.inventory.premium.free = state.inventory.gems;
  }
});

/**
 * Change `receiveMessages` type from boolean to string.
 */
migrator.addMigration(2, (state) => {
  if (!state.settings) return;
  state.settings.receiveMessages = state.settings.receiveMessages
    ? 'on'
    : 'off';
});

/**
 * Sync unlocked species with encyclopedia.
 */
migrator.addMigration(3, (state) => {
  if (!state.pet?.evolutions) return;

  if (!state.encyclopedia) {
    state.encyclopedia = {
      speciesState: {},
      speciesNotification: {},
    };
  }

  state.pet.evolutions.forEach(({ species }: any) => {
    state.encyclopedia.speciesState[species] = 'unlocked';
  });
});

/**
 * Fix duplicate duo records. And add duoFriend record.
 */
migrator.addMigration(4, (state) => {
  if (!state.history) return;

  const newHistory = [];

  // Remove duplicate duo records.
  for (const entry of state.history) {
    // Not a duo record, just add it.
    if (entry.type !== 'duo') {
      newHistory.push(entry);
      continue;
    }

    // Check if it's a duplicate.
    const existing = newHistory.find((x) => {
      if (x.type !== 'duo') return false;
      return (
        entry.friendID === x.friendID &&
        entry.name === x.name &&
        entry.age === x.age &&
        entry.species === x.species
      );
    });

    // If there is an existing entry, don't do anything.
    if (existing) {
      continue;
    }

    // Add the entry.
    newHistory.push({
      ...entry,
      // Add a default friend.
      friend: entry.friend ?? {
        name: 'Unknown',
        species: 'giraffe',
        age: 0,
      },
    });
  }

  state.history = newHistory;
});

/**
 * Migration direct decoration to tier based decoration
 */
migrator.addMigration(5, (state) => {
  if (!state.decoration) return;
  if (!state.decoration.purchased) return;
  if (Object.keys(state.decoration.purchased).length === 0) return;

  for (const itemID in state.decoration.purchased) {
    const item = state.decoration.purchased[itemID];

    // Check if items should do migration
    if (item.tier != null && Object.keys(item.tier).length !== 0) continue;

    // Item doesn't not have a tiers, we should migrate this item
    const tierId = `${itemID}_tier_2`;

    item.tier = {
      [tierId]: {
        isNew: false,
        tier: 2,
        quantity: 1,
        createdAt: item.purchasedAt,
        updatedAt: item.purchasedAt,
      },
    };
  }
});

/**
 * Migrate progression pets to control.
 */
migrator.addMigration(6, (state) => {
  // Check if player has already been migrated
  if (state.promo?.progressionMigration?.claimedRewards) return;

  // Check if there are levelUp records. This means the pet was in progression.
  const levelUpRecords = state.history?.filter(
    (record: any) => record.type === 'levelUp',
  );
  const isProgressionPet = levelUpRecords?.length > 0;

  // Pet was never in progression.
  if (!isProgressionPet) return;

  // Reset pet.
  state.petRaised = 0;
  state.highestPetAge = 0;
  state.pet = {
    name: '',
    evolutions: [],
    attributes: {
      stomach: { value: 0, lastUpdatedAt: 0 },
      love: { value: 0, lastUpdatedAt: 0 },
      dirtiness: { value: 0, lastUpdatedAt: 0 },
      urine: { value: 0, lastUpdatedAt: 0 },
      toothAcidity: { value: 0, lastUpdatedAt: 0 },
    },
    attributesEffects: {
      stomach: { duration: 0, decay: 0, startAt: 0 },
      love: { duration: 0, decay: 0, startAt: 0 },
      dirtiness: { duration: 0, decay: 0, startAt: 0 },
      urine: { duration: 0, decay: 0, startAt: 0 },
      toothAcidity: { duration: 0, decay: 0, startAt: 0 },
    },
    statusEffects: {
      dead: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      asleep: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      dying: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      evolving: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      hadVisitor: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      sick: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      toothDecay: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      scared: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      wantToGoOut: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
      daycare: { value: false, lastUpdatedAt: 0, resolutionAttempts: 0 },
    },
  };

  // Clear history.
  state.history = [];

  // Clear tutorials.
  Object.keys(state.tutorial).forEach((key) => {
    state.tutorial[key] = false;
  });

  // Clear dialogs.
  Object.keys(state.conversation.dialogs).forEach((key) => {
    state.conversation.dialogs[key].timesSeen = 0;
  });
});

/**
 * Resolve redo evolution to control.
 */
migrator.addMigration(7, (state) => {
  // Old states
  if (!state.pet || !state.encyclopedia) return;

  const evolutions = state.pet.evolutions;
  const encyclopedia = state.encyclopedia;
  const species = evolutions[evolutions.length - 1]?.species;

  if (species && encyclopedia.speciesState[species] !== 'unlocked') {
    encyclopedia.speciesState[species] = 'unlocked';
    encyclopedia.speciesNotification[species] = true;
  }

  delete state.pet.evolutionRedo;
});

/**
 * Control the visit data to prevent infinite growth
 */
migrator.addMigration(8, (state) => {
  if (!state.visit) return;

  let lastVisitAt = 0;
  let lastVisitorName = undefined;
  let lastVisitorAt = 0;
  let recentVisitsArr = [];

  const counts = {
    visitsTo: {} as any,
    visitsFrom: {} as any,
  };

  const gemGrants = {} as any;
  const recentVisitsFrom = {} as any;

  if (state.visit.visits) {
    for (let id in state.visit.visits) {
      const data = state.visit.visits[id];
      if (data.lastUpdatedAt > lastVisitAt) {
        lastVisitAt = data.lastUpdatedAt;
      }

      if (!counts.visitsTo[id]) {
        counts.visitsTo[id] = 0;
      }

      counts.visitsTo[id] = data.count;
    }
  }

  if (state.visit.visited) {
    for (let id in state.visit.visited) {
      const data = state.visit.visited[id];
      if (lastVisitorAt < data.lastUpdatedAt) {
        lastVisitorAt = data.lastUpdatedAt;
        lastVisitorName = data.name;
      }

      if (!counts.visitsFrom[id]) {
        counts.visitsFrom[id] = 0;
      }

      // Random timestamp on the 27th of April 2023
      // We only need to keep track of the last 24 hours
      if (1682690659521 - data.lastUpdatedAt < 24 * 60 * 60 * 1000) {
        gemGrants[id] = data.lastUpdatedAt;
      }

      recentVisitsArr.push({ id, last: data.lastUpdatedAt });

      counts.visitsFrom[id] = data.count;
    }
  }

  recentVisitsArr.sort((a, b) => b.last - a.last);

  // We keep track of the latest 100 visits at the time of writing
  recentVisitsArr.length = Math.min(100, recentVisitsArr.length);

  for (let visit of recentVisitsArr) {
    recentVisitsFrom[visit.id] = visit.last;
  }

  state.visit.counts = counts;
  state.visit.gemGrants = gemGrants;
  state.visit.recentVisitsFrom = recentVisitsFrom;
  state.visit.lastVisitAt = lastVisitAt;
  state.visit.lastVisitorName = lastVisitorName;

  delete state.visit.visited;
  delete state.visit.visits;
});

/**
 * Remove deprecated fields.
 */
migrator.addMigration(9, (state) => {
  if (state.muteNotifications != null) {
    delete state.muteNotifications;
  }

  if (state.pet?.evolutions != null) {
    state.pet.evolutions.forEach((evolution: any) => {
      delete evolution.claimedRewards;
      delete evolution.level;
    });
  }

  if (state.inventory) {
    delete state.inventory.gems;
    delete state.inventory.treasure;
  }

  if (state.progression) {
    delete state.progression.purchased;
    delete state.progression.selected;
  }
});

/**
 * Remove deprecated referral state.
 */
migrator.addMigration(10, (state) => {
  delete state.referral;
});

/**
 * Make sure players on the last level are bumped to the next level.
 */
migrator.addMigration(11, (state) => {
  if (!state?.match2?.level || state.match2.level !== 525) return;
  state.match2.level++;
});

/**
 * Migrate slot users to match2.
 */
migrator.addMigration(12, (state) => {
  // ------------------------
  // Make sure we don't executed migration on new users
  // ------------------------

  // No match2 state. Skip.
  if (!state.match2) return;

  // No migration state. Skip.
  if (!state.m2Migration) return;

  // Already migrated. Skip.
  if (state.m2Migration.finished) return;

  // Assume that all match2 players have played at least 1 level.
  const hasPlayedMatch2 = state.match2.level > 1;

  // If user has logged in after match2 migration, mark as migrated.
  const migrationDate = 1694563200000; // 14th of september 2023 UTC
  const hasLoggedInAfterMigration = state.lastSessionAt > migrationDate;

  if (hasPlayedMatch2 || hasLoggedInAfterMigration || !state.lastSessionAt) {
    return;
  }

  // ------------------------
  // Actual migration sequence
  // ------------------------

  // Already migrated. Skip.
  if (state.m2Migration.finished) return;

  // Fixed migration date.
  const now = 1700179200000; // 17th of november 2023 UTC

  // Step #1: Reset negative status effects: sick/death/dying
  resetNegativeEffects();

  // Step #2: Paid gems compensation
  paidGemsCompensation();

  // Step #3: Free gems balance fix migration and compensation
  // We decided to skip this step!

  // Step #4: Pet progression migration
  if (state.costume) {
    petProgressionMigration();
  }

  // Step #5: Pet decoration migration
  // We decided to skip this step! Because of hight complexity of implementation

  // Step #6: Accessories migration and compensation
  if (state.accessory) {
    accessoriesMigration();
  }

  // Step #7: Reset energy
  if (state.inventory) {
    resetEnergy();
  }

  // Step #8: Special compensation
  if (state.m2Migration.specialCompensationBucket !== 'none') {
    specialCompensation();
  }

  // Mark migration as finished.
  state.m2Migration.finished = true;

  // Set flag so we can show popup.
  state.m2Migration.shouldShowMigrationPopup = true;

  // ------------------------
  // Migration steps functions
  // ------------------------

  // Step #1: Reset negative status effects: sick/death/dying
  function resetNegativeEffects() {
    state.admin?.push({
      resetPetNegativeEffects: true,
      reason: 'Match 2 migration negative status reset',
      claimed: false,
      timestamp: now,
    });
  }

  // Step #2: Paid gems compensation
  function paidGemsCompensation() {
    const paidGemsCompensationRewards = {
      // 20x each of Hammer, Cup Rocket, Airplane, Pinwheel, Firecracker, Water Balloon, and Lollipop.
      GROUP_MORE_THAN_10K: [
        { type: 'boosters', subType: 'dart', amount: 20 }, // Hammer - destroy 1 block
        { type: 'boosters', subType: 'drill', amount: 20 }, // Cup rocket - destroy column
        { type: 'boosters', subType: 'bullet', amount: 20 }, // Airplane - destroy row
        { type: 'boosters', subType: 'roulette', amount: 20 }, // Pinwheel - shuffle
        { type: 'powerups', subType: 'rocket', amount: 20 }, // Firecracker - destroy row or column
        { type: 'powerups', subType: 'bomb', amount: 20 }, // Water Bomb - destroy surrounding blocks
        { type: 'powerups', subType: 'cube', amount: 20 }, // Lollipop - destroy all blocks of same color
      ],
      // 10x each of Hammer, Cup Rocket, Airplane, Pinwheel, Firecracker, Water Balloon, and Lollipop
      GROUP_LESS_THAN_10k: [
        { type: 'boosters', subType: 'dart', amount: 10 }, // Hammer - destroy 1 block
        { type: 'boosters', subType: 'drill', amount: 10 }, // Cup rocket - destroy column
        { type: 'boosters', subType: 'bullet', amount: 10 }, // Airplane - destroy row
        { type: 'boosters', subType: 'roulette', amount: 10 }, // Pinwheel - shuffle
        { type: 'powerups', subType: 'rocket', amount: 10 }, // Firecracker - destroy row or column
        { type: 'powerups', subType: 'bomb', amount: 10 }, // Water Bomb - destroy surrounding blocks
        { type: 'powerups', subType: 'cube', amount: 10 }, // Lollipop - destroy all blocks of same color
      ],
      // 10x each of Firecracker, Water Balloon, and Lollipop.
      DEFAULT: [
        { type: 'powerups', subType: 'rocket', amount: 10 }, // Firecracker - destroy row or column
        { type: 'powerups', subType: 'bomb', amount: 10 }, // Water Bomb - destroy surrounding blocks
        { type: 'powerups', subType: 'cube', amount: 10 }, // Lollipop - destroy all blocks of same color
      ],
    } as const;

    const bucket = state.m2Migration.bucket ?? 'default';
    let rewards: (typeof paidGemsCompensationRewards)[keyof typeof paidGemsCompensationRewards] =
      paidGemsCompensationRewards.DEFAULT;
    switch (bucket) {
      case 'moreThan10k':
        rewards = paidGemsCompensationRewards['GROUP_MORE_THAN_10K'];
        break;
      case 'lessThan10K':
        rewards = paidGemsCompensationRewards['GROUP_LESS_THAN_10k'];
        break;
    }

    grantMatch2Rewards(rewards);
  }

  // Step #4: Pet progression migration
  function petProgressionMigration() {
    // Set current evolution's species to match costume species.
    if (state.costume.equipped) {
      const species =
        state.costume?.inventory?.[state.costume.equipped].species;
      const evolutionIndex = state.pet?.evolutions?.length ?? 0;
      const currentEvolution = state.pet?.evolutions?.[evolutionIndex - 1];
      if (currentEvolution && species) {
        currentEvolution.species = species;
      }
      delete state.costume.equipped;
    }

    state.costume.inventory = {};
    state.costume.tickets = {};
  }

  // Step #6: Accessories migration and compensation
  function accessoriesMigration() {
    state.accessory.purchased = {};
    state.accessory.selected = [];
  }

  // Step #7: Reset energy
  function resetEnergy() {
    state.inventory.energy = 5;
    state.inventory.energyRechargeStartTime = now;
    // Grant 60 minutes of unlimited energy on next login.
    state.admin?.push({
      match2Consumables: {
        type: 'energy',
        duration: 60 * 60 * 1000, // 60 minutes
      },
      reason: 'Match 2 migration energy compensation',
      claimed: false,
      timestamp: now,
    });
  }

  // Step #8: Special compensation
  function specialCompensation() {
    type Item =
      | '1_wallpaper'
      | '2_floor'
      | '3_above_fireplace'
      | '4_painting'
      | '5_fireplace'
      | '6_tree'
      | '7_couch'
      | '8_sidetable';

    type Bucket =
      | '2501_5000_FreeGems'
      | '5001_10000_FreeGems'
      | '10_001_20000_FreeGems'
      | '20001_FreeGems'
      | '10k_PaidGems'
      | 'none';

    type Tier = 3 | 4;
    type ItemID =
      | `living_18_candy_house_${Item}_a`
      | `living_17_halloween_${Item}_a`;
    type tierID =
      | `living_18_candy_house_${Item}_a_tier_${Tier}`
      | `living_17_halloween_${Item}_a_tier_${Tier}`;

    type Reward = {
      theme: 'halloween' | 'candy';
      tier: Tier;
      items: [ItemID, tierID][];
    };

    const items: Item[] = [
      '1_wallpaper',
      '2_floor',
      '3_above_fireplace',
      '4_painting',
      '5_fireplace',
      '6_tree',
      '7_couch',
      '8_sidetable',
    ];
    const halloweenTier3 = items.map(
      (item) =>
        [
          `living_17_halloween_${item}_a`,
          `living_17_halloween_${item}_a_tier_3`,
        ] as [ItemID, tierID],
    );
    const halloweenTier4 = items.map(
      (item) =>
        [
          `living_17_halloween_${item}_a`,
          `living_17_halloween_${item}_a_tier_4`,
        ] as [ItemID, tierID],
    );
    const candyTier3 = items.map(
      (item) =>
        [
          `living_18_candy_house_${item}_a`,
          `living_18_candy_house_${item}_a_tier_3`,
        ] as [ItemID, tierID],
    );
    const candyTier4 = items.map(
      (item) =>
        [
          `living_18_candy_house_${item}_a`,
          `living_18_candy_house_${item}_a_tier_4`,
        ] as [ItemID, tierID],
    );

    const buckets: Record<Bucket, Reward[]> = {
      '2501_5000_FreeGems': [
        {
          theme: 'halloween',
          tier: 3,
          items: halloweenTier3,
        },
      ],
      '5001_10000_FreeGems': [
        {
          theme: 'halloween',
          tier: 3,
          items: halloweenTier3,
        },
        {
          theme: 'candy',
          tier: 3,
          items: candyTier3,
        },
      ],
      '10_001_20000_FreeGems': [
        {
          theme: 'halloween',
          tier: 4,
          items: halloweenTier4,
        },
        {
          theme: 'candy',
          tier: 3,
          items: candyTier3,
        },
      ],
      '20001_FreeGems': [
        {
          theme: 'halloween',
          tier: 4,
          items: halloweenTier4,
        },
        {
          theme: 'candy',
          tier: 4,
          items: candyTier4,
        },
      ],
      '10k_PaidGems': [
        {
          theme: 'halloween',
          tier: 4,
          items: halloweenTier4,
        },
        {
          theme: 'candy',
          tier: 4,
          items: candyTier4,
        },
      ],
      none: [],
    };

    const compensationBucket: Bucket =
      state.m2Migration.specialCompensationBucket ?? 'none';

    console.log('compensationBucket', compensationBucket);

    const rewards = buckets[compensationBucket];
    if (!rewards) return;

    rewards.forEach((reward) => {
      const { items, tier } = reward;

      if (!items) {
        return;
      }

      items?.forEach((item) => {
        const [id, tierId] = item;
        addDecorationItem(id, tierId, tier);
      });
    });

    // Set special compensation flag
    state.m2Migration.specialCompensation = true;

    // Part of step #8: Special compensation
    function addDecorationItem(id: ItemID, tierId: tierID, tierNumber: number) {
      let item = state.decoration.purchased[id];

      if (!item) {
        state.decoration.purchased[id] = {
          purchasedAt: now,
          isNew: true,
          tier: {},
        };
      }
      item = state.decoration.purchased[id];

      const tierData = item.tier[tierId];

      // New item.
      if (!tierData) {
        item.tier[tierId] = {
          tier: tierNumber,
          createdAt: now,
          updatedAt: now,
          isNew: true,
          quantity: 1,
        };

        return;
      }

      // Update existing item
      tierData.quantity += 1;
      tierData.updatedAt = now;
    }
  }

  // Part of step #2: Paid gems compensation
  function grantMatch2Rewards(rewards: any) {
    for (const reward of rewards) {
      const { boosters, powerups } = state.match2 as any;
      switch (reward.type) {
        case 'bPoints':
          state.inventory.points += reward.amount;
          break;
        case 'boosters':
          if (!boosters[reward.subType]) {
            boosters[reward.subType] = 0;
          }
          boosters[reward.subType] += reward.amount;
          break;
        case 'powerups':
          if (!powerups[reward.subType]) {
            powerups[reward.subType] = 0;
          }
          powerups[reward.subType] += reward.amount;
          break;
      }
    }
  }
});

migrator.addMigration(13, (state) => {
  delete state.match2?.flashSale;
});

migrator.addMigration(14, (state) => {
  delete state.buddies;
});

// Ensure tutorial.treasure and evolutionRedo are fully deleted
migrator.addMigration(15, (state) => {
  if (state.pet?.evolutionRedo) {
    delete state.pet.evolutionRedo;
  }
  if (state.tutorial?.treasure) {
    delete state.tutorial.treasure;
  }
});

// Ensure tutorial.treasure and pet.evolutionRedo are fully deleted, even if they are set to false
migrator.addMigration(16, (state) => {
  if (state.pet) {
    if ('evolutionRedo' in state.pet) {
      delete state.pet.evolutionRedo;
    }
  }

  if (state.tutorial) {
    if ('treasure' in state.tutorial) {
      delete state.tutorial.treasure;
    }
  }
});

// Move current `pendingPayment` object filed to a new field array `pendingPayments`
migrator.addMigration(17, (state) => {
  if (!state) return;

  // If there is no pendingPayment, skip
  if (!state.pendingPayment) return;

  // Remove pendingPayment if no platformType
  if (!state.pendingPayment.platformType) {
    delete state.pendingPayment;
    return;
  }

  // Remove pendingPayment if no paymentMethod
  if (!state.pendingPayment.paymentMethod) {
    delete state.pendingPayment;
    return;
  }

  // Remove pendingPayment it xsolla
  if (state.pendingPayment.platformType === 'xsolla') {
    delete state.pendingPayment;
    return;
  }

  // Remove pendingPayment if no platformId
  if (!state.pendingPayment.platformId) {
    delete state.pendingPayment;
    return;
  }

  // Remove pendingPayment if no productId
  if (!state.pendingPayment.productId) {
    delete state.pendingPayment;
    return;
  }

  // Add type check just in case
  type PendingPayment = {
    id: string;
    currency: string;
    price: number;
    paymentProvider: string;
    paymentMethod: string;
    creditCardBrand?: string;
    purchaseId: string;
    productId: string;
    createdAt: number;
    analytics: {
      feature: string;
      subFeature: string;
    };
    webPayAnalytics: {
      elapsedSecondsOpen: number;
      sessionOpenSequence: number;
      sessionSuccessSequence: number;
      instanceSelectSequence: number;
      sessionSuccessSequenceForMethod: number;
      instanceSelectSequenceForMethod: number;
      hasAgeCheckbox: boolean;
    };
    webPayMethodTimestamp: number;
    updatedAt: number;
    status: 'pending' | 'completed' | 'failed';
  };

  // Create new pendingPayments and put old data here
  const newPendingPayment: PendingPayment = {
    id: `${state.pendingPayment.platformType}_${state.pendingPayment.platformId}`,
    currency: 'JPY', // Assume all pending payments are in JPY
    price: 0, // We  don't price, but we need this only for analytics
    paymentProvider: 'komoju', // Formerly platformType
    paymentMethod: state.pendingPayment.paymentMethod,
    creditCardBrand: state.pendingPayment.creditCardBrand, // Optional
    purchaseId: state.pendingPayment.platformId, // Formerly platformId
    productId: state.pendingPayment.productId,
    createdAt: state.pendingPayment.createdAt ?? 1733758347481,
    analytics: {
      feature: state.pendingPayment?.analytics?.feature ?? 'feature',
      subFeature: state.pendingPayment?.analytics?.subFeature ?? 'subFeature',
    },
    webPayAnalytics: {
      elapsedSecondsOpen:
        state.pendingPayment.webPayAnalytics?.elapsedSecondsOpen ?? 0,
      sessionOpenSequence:
        state.pendingPayment.webPayAnalytics?.sessionOpenSequence ?? 0,
      sessionSuccessSequence:
        state.pendingPayment.webPayAnalytics?.sessionSuccessSequence ?? 0,
      instanceSelectSequence:
        state.pendingPayment.webPayAnalytics?.instanceSelectSequence ?? 0,
      sessionSuccessSequenceForMethod:
        state.pendingPayment.webPayAnalytics?.sessionSuccessSequenceForMethod ??
        0,
      instanceSelectSequenceForMethod:
        state.pendingPayment.webPayAnalytics?.instanceSelectSequenceForMethod ??
        0,
      hasAgeCheckbox:
        state.pendingPayment.webPayAnalytics?.hasAgeCheckbox ?? true,
    },
    webPayMethodTimestamp:
      state.pendingPayment.webPayMethodTimestamp ?? 1733758347481,
    updatedAt: 1733758347481, // Mon 9 Dec 2024
    status: 'pending', // Default status
  };

  // If new field somehow undefined
  if (!state.pendingPayments) {
    state.pendingPayments = [];
  }

  // Add new pendingPayment to pendingPayments
  state.pendingPayments.push(newPendingPayment);

  // Remove old pendingPayment
  delete state.pendingPayment;
});

// Initialize leagues all-time score
migrator.addMigration(18, (state) => {
  if (!state) return;
  if (!state.match2) return;

  // Skip if already initialized.
  if (state.match2.leaguesAllTimeScore) return;

  // Initialize all-time score to 0.
  state.match2.leaguesAllTimeScore = 0;

  // Loop through all leagues and sum up the scores.
  for (const id in state.match2.leagues) {
    const league = state.match2.leagues[id];
    if (!league) continue;
    // Add league score to all-time score.
    state.match2.leaguesAllTimeScore += league.score;
  }
});

// Separate winStreak from buruStreak.
migrator.addMigration(19, (state) => {
  if (!state) return;
  if (!state.match2) return;

  state.match2.buruStreak = {
    value: state.match2.winStreak ?? 0,
  };
});

// Cleanup social friends state as part of mini app migration
migrator.addMigration(20, (state) => {
  if (!state) return;
  if (!state.socialFriends) return;

  // Remove all social friends.
  state.socialFriends = {};
});

// Create interaction log for inactive users
migrator.addMigration(21, (state) => {
  if (!state) return;

  const friendsIds = state.friends ? Object.keys(state.friends) : [];
  const now = 1750346650000; // Thu Jun 19 2025 17:24:10 GMT+0200 (Central European Summer Time)

  /**
   * Simple and safe decodePacket function for migration code
   * No dependencies, comprehensive error handling, all-in-one function
   * We need decode invited users ID's to compere with friends IDs
   */
  const decodePacket = (encodedPacket: string) => {
    // Input validation
    if (typeof encodedPacket !== 'string') {
      return encodedPacket;
    }

    if (encodedPacket.length === 0) {
      return encodedPacket;
    }

    try {
      // Step 1: Reverse the XOR cipher
      let withoutXor = '';
      for (let i = 0; i < encodedPacket.length; i++) {
        const charCode = encodedPacket.charCodeAt(i);

        // Convert to custom base64 value
        let b64Value: number;
        if (charCode === 43)
          b64Value = 0; // '+'
        else if (charCode === 47)
          b64Value = 1; // '/'
        else if (charCode >= 48 && charCode <= 57)
          b64Value = charCode - 48 + 2; // '0'-'9'
        else if (charCode >= 65 && charCode <= 90)
          b64Value = charCode - 65 + 12; // 'A'-'Z'
        else if (charCode >= 97 && charCode <= 122)
          b64Value = charCode - 97 + 38; // 'a'-'z'
        else throw new Error(`Invalid character code: ${charCode}`);

        // Apply XOR with 0x1c (symmetric operation)
        const xorResult = b64Value ^ 0x1c;

        // Convert back to character code
        let resultCharCode: number;
        if (xorResult === 0)
          resultCharCode = 43; // '+'
        else if (xorResult === 1)
          resultCharCode = 47; // '/'
        else if (xorResult >= 2 && xorResult <= 11)
          resultCharCode = xorResult - 2 + 48; // '0'-'9'
        else if (xorResult >= 12 && xorResult < 38)
          resultCharCode = xorResult - 12 + 65; // 'A'-'Z'
        else if (xorResult >= 38 && xorResult < 64)
          resultCharCode = xorResult - 38 + 97; // 'a'-'z'
        else throw new Error(`Invalid base64 value: ${xorResult}`);

        withoutXor += String.fromCharCode(resultCharCode);
      }

      // Step 2: Add back base64 padding
      const remainder = withoutXor.length % 4;
      let withPadding = withoutXor;
      if (remainder === 2) {
        withPadding += '==';
      } else if (remainder === 3) {
        withPadding += '=';
      } else if (remainder === 1) {
        throw new Error('Invalid base64 string length');
      }

      // Step 3: Decode base64
      // Validate base64 format
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(withPadding)) {
        throw new Error('Invalid base64 format');
      }

      // Use built-in atob if available (browser/modern Node.js)
      if (typeof atob !== 'undefined') {
        return atob(withPadding);
      }

      // Fallback base64 decoder for environments without atob
      const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
      let result = '';

      for (let i = 0; i < withPadding.length; i += 4) {
        const encoded1 = chars.indexOf(withPadding[i]);
        const encoded2 = chars.indexOf(withPadding[i + 1]);
        const encoded3 = chars.indexOf(withPadding[i + 2]);
        const encoded4 = chars.indexOf(withPadding[i + 3]);

        if (encoded1 === -1 || encoded2 === -1) {
          throw new Error('Invalid base64 character');
        }

        const bitmap =
          (encoded1 << 18) |
          (encoded2 << 12) |
          ((encoded3 >= 0 ? encoded3 : 0) << 6) |
          (encoded4 >= 0 ? encoded4 : 0);

        result += String.fromCharCode((bitmap >> 16) & 255);

        if (encoded3 >= 0) {
          result += String.fromCharCode((bitmap >> 8) & 255);
        }

        if (encoded4 >= 0) {
          result += String.fromCharCode(bitmap & 255);
        }
      }

      return result;
    } catch (error) {
      return encodedPacket;
    }
  };

  /**
   * Create friends interaction log record
   */
  const addFriendInteraction = (
    state: any,
    opts: {
      type:
        | 'gift'
        | 'visit'
        | 'invite'
        | 'duo'
        | 'continue'
        | 'rescueQuest'
        | 'fairyForest';
      friendId: string;
    },
    now: number,
  ) => {
    const { type, friendId } = opts;

    if (!state.friendsInteraction) {
      state.friendsInteraction = {};
    }

    if (!state.friendsInteraction[friendId]) {
      state.friendsInteraction[friendId] = {};
    }

    if (!state.friendsInteraction[friendId][type]) {
      state.friendsInteraction[friendId][type] = now;
    }
  };

  /**
   * Create Buru friend record
   */
  const createBuruFriend = (id: string) => {
    // Create friends list if not exists
    if (!state.buruFriends) {
      state.buruFriends = {};
    }

    if (state.buruFriends[id]) {
      return; // Already in friends list
    }

    // Add friend to friends list
    state.buruFriends[id] = {
      addedAt: now,
    };
  };

  // Save existing gift interactions
  Object.keys(state?.gifts?.sent ?? {}).forEach((id) =>
    addFriendInteraction(state, { type: 'gift', friendId: id }, now),
  );
  Object.keys(state?.gifts?.received ?? {}).forEach((id) =>
    addFriendInteraction(state, { type: 'gift', friendId: id }, now),
  );

  // Save invites interactions
  Object.keys(state?.social?.invites ?? {}).forEach((id) =>
    addFriendInteraction(
      state,
      { type: 'invite', friendId: decodePacket(id) }, // Invites ids is masked and we need to decode it
      now,
    ),
  );
  if (state?.social?.referral?.invitedBy) {
    addFriendInteraction(
      state,
      { type: 'invite', friendId: state.social.referral.invitedBy },
      now,
    );
  }

  // Save existing visit interactions
  Object.keys(state?.visit?.recentVisitsFrom ?? {})
    .filter((id) => friendsIds.includes(id))
    .forEach((id) =>
      addFriendInteraction(state, { type: 'visit', friendId: id }, now),
    );
  Object.keys(state?.visit?.recentVisitsTo ?? {})
    .filter((id) => friendsIds.includes(id))
    .forEach((id) =>
      addFriendInteraction(state, { type: 'visit', friendId: id }, now),
    );

  // Save Duo interactions
  (state?.duo?.invitations?.sent ?? []).forEach(
    (invitation: { playerId: any }) => {
      addFriendInteraction(
        state,
        { type: 'duo', friendId: invitation.playerId },
        now,
      );
    },
  );
  (state?.duo?.invitations?.received ?? []).forEach(
    (invitation: { playerId: any }) => {
      addFriendInteraction(
        state,
        { type: 'duo', friendId: invitation.playerId },
        now,
      );
    },
  );

  // Save rescue quest interactions
  const entries = state?.rescueQuest?.entries ?? {};
  Object.keys(entries).forEach((id) => {
    const entry = entries[id];
    Object.keys(entry.participants ?? {}).forEach((participantId) =>
      addFriendInteraction(
        state,
        { type: 'rescueQuest', friendId: participantId },
        now,
      ),
    );
    Object.keys(entry?.invitesSent ?? {}).forEach((inviteId) =>
      addFriendInteraction(
        state,
        { type: 'rescueQuest', friendId: inviteId },
        now,
      ),
    );
    Object.keys(entry?.invitesReceived ?? {}).forEach((inviteId) =>
      addFriendInteraction(
        state,
        { type: 'rescueQuest', friendId: inviteId },
        now,
      ),
    );
  });

  // Match friends interactions with state.friends
  // Check if interaction is in friends list
  for (const id of Object.keys(state.friendsInteraction || {})) {
    // Check is id start with U, all LINE IDs start with U
    // For invites we decode user ID, if it do no  start with U it means it is encoded and we need to decode it first
    const friendId = id.startsWith('U') ? id : decodePacket(id);
    if (friendId && state.friendsInteraction[id]) {
      const isFriend = friendsIds.includes(friendId);
      state.friendsInteraction[id].isFriend = isFriend;
      // Create buru friend if interaction is from friend
      if (isFriend && friendId.startsWith('U')) createBuruFriend(friendId);
    }
  }
});

/**
 * Delete deprecated friend-related state fields.
 * @see https://github.com/play-co/buruburu/issues/9496
 */
migrator.addMigration(22, (state) => {
  if (!state) return;

  // Delete deprecated state fields related to removed features
  // Using 'in' operator to properly handle falsy values (see migration #16)
  if ('friends' in state) {
    delete state.friends; // Platform friends from LINE social API (socialSchema)
  }
  if ('socialFriends' in state) {
    delete state.socialFriends; // Legacy social friends backup (friendsSchema)
  }
  if ('rescueQuest' in state) {
    delete state.rescueQuest; // Rescue quest feature state (rescueQuestSchema)
  }
});

/**
 * Close out dormant Champions League V2 entries for the retired '3550'
 * event, so the removed launch-time resolver and the V1 end/claim flows
 * never fire for them. No rewards are granted. Phase 1 entries are left
 * alone and keep their regular V1 claim flow: the old detector
 * (isChampionsLeagueV2Event) required phase > 1 and intentionally treated
 * phase 1 entries as V1, since V2 joiners who never played are
 * indistinguishable from V1 players.
 * @see https://github.com/play-co/buruburu/issues/10056
 */
migrator.addMigration(23, (state) => {
  const league = state?.match2?.leagues?.['3550'];
  if (!league || league.claimed) return;

  if ((league.phase ?? 1) > 1) {
    league.claimed = true;
  }
});

/**
 * Fold the deprecated `conversation.dialogs` counters into `dialogCounts`
 * (`dialogs['alpaca_001'] = { timesSeen: 3 }` is `dialogCounts.alpaca['1'] = 3`,
 * `dialogs.want_to_add_friend_dog` is `dialogCounts.dog.want_to_add_friend`)
 * and drop `dialogs`. `dialogs` is what every client read, and what a client
 * that predates `dialogCounts` cleared on rebirth, so `dialogCounts` is
 * rebuilt from it rather than merged: an entry only `dialogCounts` has is a
 * dead pet's.
 */
migrator.addMigration(24, (state) => {
  const conversation = state?.conversation;
  if (!conversation || typeof conversation !== 'object') return;
  if (!('dialogs' in conversation)) return;

  const dialogs = conversation.dialogs;
  delete conversation.dialogs;
  if (!dialogs || typeof dialogs !== 'object') return;

  const dialogCounts: Record<string, Record<string, number>> = {};
  conversation.dialogCounts = dialogCounts;
  const wantToAddFriendPrefix = 'want_to_add_friend_';
  for (const id of Object.keys(dialogs)) {
    const timesSeen = dialogs[id]?.timesSeen;
    if (typeof timesSeen !== 'number') continue;

    let species: string;
    let key: string;
    if (id.startsWith(wantToAddFriendPrefix)) {
      species = id.slice(wantToAddFriendPrefix.length);
      key = 'want_to_add_friend';
    } else {
      const separator = id.lastIndexOf('_');
      if (separator < 0) continue;
      species = id.slice(0, separator);
      key = String(Number(id.slice(separator + 1)));
      if (key === 'NaN') continue;
    }

    // The id was client-supplied: a species like `__proto__` or `constructor` would resolve
    // to an inherited object here, and writing into that pollutes every object in the process.
    if (species in dialogCounts && !Object.hasOwn(dialogCounts, species))
      continue;

    const counts = (dialogCounts[species] ??= {});
    counts[key] = timesSeen;
  }
});

// Add new migrations right above this line. Do *NOT* modify existing migrations.

export default migrator;
